import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { tokens } from '@tradelogic/ui-tokens';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface DashboardWidget {
  readonly widgetId: string;
  readonly title: string;
  readonly value: string;
}

/** Tenant branding sourced at runtime from t003_branding_configuration. When a
 *  field is absent the ui-tokens default palette is used as the fallback, so a
 *  branding row change re-colours the dashboard with no code change (Q13/C9). */
interface DashboardBranding {
  readonly primaryColor?: string;
  readonly secondaryColor?: string;
  readonly accentColor?: string;
}

interface DashboardConfig {
  readonly dashboardConfigurationId: string;
  readonly dashboardCode: string;
  readonly dashboardName: string;
  readonly branding: DashboardBranding;
  readonly widgets: readonly DashboardWidget[];
}

/** L16.04 — Dashboard view (`/dashboards/:dashboardId`). Renders tenant
 *  primary/secondary/accent colours from t003_branding_configuration via the
 *  ui-tokens runtime palette fallback. */
export function M16DashboardViewPage(): ReactNode {
  const { t } = useTranslation();
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const { token } = useAuth();
  const [config, setConfig] = useState<DashboardConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<DashboardConfig>(`/v1/m16/dashboards/configs/${dashboardId}`, { token })
      .then((r) => { if (alive) setConfig(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [dashboardId, token]);

  if (error) return <p role="alert">{error}</p>;
  if (!config) return <p>{t('m16.dashboardView.loading')}</p>;

  const primary = config.branding.primaryColor ?? tokens.color.brand[600];
  const secondary = config.branding.secondaryColor ?? tokens.color.brand[400];
  const accent = config.branding.accentColor ?? tokens.color.semantic.warning;

  return (
    <main aria-label="dashboard-view" style={{ borderTop: `4px solid ${primary}` }}>
      <h1 style={{ color: primary }}>{config.dashboardName}</h1>
      <ul aria-label="dashboard-widgets" style={{ listStyle: 'none', padding: 0 }}>
        {config.widgets.map((w) => (
          <li
            key={w.widgetId}
            data-testid={`widget-${w.widgetId}`}
            style={{ borderLeft: `4px solid ${secondary}`, padding: 8, marginBottom: 8 }}
          >
            <span style={{ color: accent }}>{w.title}</span>: <strong>{w.value}</strong>
          </li>
        ))}
      </ul>
    </main>
  );
}
